import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { supabase, sendConfirmationEmail } from '../lib/supabase';
import { toast } from 'sonner';
import { Mail, Lock, LogIn, UserPlus, Scissors, AlertCircle, CreditCard } from 'lucide-react';
import { Logo } from './Logo';
import { useLanguage } from '../lib/LanguageContext';
import { useTheme } from '../lib/ThemeContext';
import { authSchema } from '../lib/validations';
import { z } from 'zod';

type AuthFormData = z.infer<typeof authSchema>;

const PasswordStrengthIndicator = ({ password, strength, strengthLabels, strengthColors }: { password: string, strength: number, strengthLabels: string[], strengthColors: string[] }) => {
  if (!password) return null;
  return (
    <div className="mt-2 px-4">
      <div className="flex gap-1 h-1 mb-1">
        {[...Array(5)].map((_, i) => (
          <div 
            key={i} 
            className={`flex-1 rounded-full transition-all duration-500 ${
              i < strength ? strengthColors[strength - 1] : 'bg-zinc-100 dark:bg-zinc-800'
            }`}
          />
        ))}
      </div>
      <p className={`text-[9px] font-black uppercase tracking-widest text-left ${
        strength > 0 ? strengthColors[strength - 1].replace('bg-', 'text-') : 'text-zinc-400 dark:text-zinc-300'
      }`}>
        {strengthLabels[Math.max(0, strength - 1)]}
      </p>
    </div>
  );
};

export default function Auth({ 
  onAuthSuccess, 
  role = 'barber_admin', 
  initialIsSignUp = false,
  shopId,
  shopName,
  planId = 'monthly',
  onLogoClick
}: { 
  onAuthSuccess: () => void, 
  role?: 'barber_admin' | 'customer', 
  initialIsSignUp?: boolean,
  shopId?: string,
  shopName?: string,
  planId?: string,
  onLogoClick?: () => void
}) {
  const { t, language } = useLanguage();
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [currentRole, setCurrentRole] = useState<'barber_admin' | 'customer'>(role);
  const [isSignUp, setIsSignUp] = useState(initialIsSignUp);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [showConfirmationMessage, setShowConfirmationMessage] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset
  } = useForm<AuthFormData>({
    resolver: zodResolver(authSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      companyName: '',
      address: '',
      phone: '',
      fullName: '',
    }
  });

  // Log validation errors for debugging
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      console.log("Form Validation Errors:", errors);
    }
  }, [errors]);

  const password = watch('password');
  const email = watch('email');

  React.useEffect(() => {
    // Check for confirmation success in URL
    const params = new URLSearchParams(window.location.search);
    if (params.get('confirmed') === 'true') {
      setIsConfirmed(true);
      toast.success(t('auth.emailConfirmed') || 'Email confirmed successfully! You can now sign in.');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Listen for password recovery event
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsResettingPassword(true);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const getPasswordStrength = (pass: string) => {
    if (!pass) return 0;
    let strength = 0;
    if (pass.length >= 8) strength++;
    if (/[A-Z]/.test(pass)) strength++;
    if (/[a-z]/.test(pass)) strength++;
    if (/[0-9]/.test(pass)) strength++;
    if (/[^A-Za-z0-9]/.test(pass)) strength++;
    return strength;
  };

  const strength = getPasswordStrength(password);
  const strengthLabels = [
    t('auth.strengthVeryWeak') || 'Very Weak',
    t('auth.strengthWeak') || 'Weak',
    t('auth.strengthMedium') || 'Medium',
    t('auth.strengthStrong') || 'Strong',
    t('auth.strengthVeryStrong') || 'Very Strong'
  ];

  const strengthColors = [
    'bg-red-500',
    'bg-orange-500',
    'bg-yellow-500',
    'bg-blue-500',
    'bg-emerald-500'
  ];


  React.useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendCountdown > 0) {
      timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  React.useEffect(() => {
    setCurrentRole(role);
  }, [role]);

  const handleAuth = async (formData: AuthFormData) => {
    setLoading(true);

    try {
      if (isResettingPassword) {
        const { error } = await supabase.auth.updateUser({ password: formData.password });
        if (error) throw error;
        toast.success(t('auth.passwordUpdated'));
        setIsResettingPassword(false);
        onAuthSuccess();
      } else if (isForgotPassword) {
        const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
          redirectTo: window.location.origin,
        });
        if (error) throw error;
        toast.success(t('auth.resetLinkSent'));
        setIsForgotPassword(false);
      } else if (isSignUp) {
        // STRICT VALIDATION: Check if email exists before proceeding
        const checkRes = await fetch('/api/check-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: formData.email })
        });
        const { exists } = await checkRes.json();
        
        if (exists) {
          toast.error(t('auth.emailExists') || 'This email is already registered.');
          setLoading(false);
          return;
        }

        // PAYMENT-FIRST FLOW for Barber Admins
        if (currentRole === 'barber_admin') {
          const response = await fetch('/api/create-checkout-session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              planId,
              agreedToTerms: true,
              registrationData: {
                email: formData.email,
                password: formData.password,
                shopData: {
                  firstName: formData.firstName,
                  lastName: formData.lastName,
                  companyName: formData.companyName,
                  address: formData.address,
                  phone: formData.phone
                }
              }
            }),
          });

          const data = await response.json();
          if (data.url) {
            window.location.href = data.url;
            return; // Redirecting, no need to continue
          } else {
            throw new Error(data.error || 'Failed to initiate payment');
          }
        }

        // Standard flow for customers
        const metadata: any = {
          role: currentRole,
          phone: formData.phone,
          full_name: formData.fullName,
        };

        if (shopId) {
          metadata.barber_shop_id = shopId;
        }

        // Use our manual signup endpoint to send custom confirmation email via Resend
        const signupRes = await fetch('/api/auth/signup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            metadata
          })
        });

        const signupData = await signupRes.json();
        
        if (!signupRes.ok) {
          throw new Error(signupData.error || 'Failed to sign up');
        }

        if (signupData.user) {
          setShowConfirmationMessage(true);
          setResendCountdown(60);
          toast.success(t('auth.checkEmail'));
          
          // Notification to super admin (still useful)
          sendConfirmationEmail('devthines@gmail.com', 'admin_new_user', {
            customerName: formData.fullName,
            role: currentRole
          }).catch(err => console.error("Admin notification failed:", err));
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({ 
          email: formData.email, 
          password: formData.password 
        });
        if (error) {
          if (error.message.toLowerCase().includes('email not confirmed') || error.status === 400) {
            if (error.message.toLowerCase().includes('email not confirmed')) {
              toast.error(t('auth.verifyEmail'));
              setShowConfirmationMessage(true);
              setResendCountdown(60);
              return;
            }
          }
          throw error;
        }
        onAuthSuccess();
      }
    } catch (error: any) {
      console.error("Authentication Error:", error);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (resendCountdown > 0) return;
    
    setLoading(true);
    try {
      const response = await fetch('/api/auth/resend-confirmation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to resend email');

      setResendCountdown(60);
      toast.success(t('auth.checkEmail'));
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (showConfirmationMessage) {
    return (
      <div className="w-full max-w-xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="bg-white dark:bg-zinc-900 p-8 sm:p-10 rounded-[2.5rem] sm:rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl text-center">
          <div className="inline-block p-4 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-500 rounded-2xl mb-6">
            <Mail size={32} aria-hidden="true" />
          </div>
          <h2 className="text-3xl font-black uppercase tracking-tighter mb-4 dark:text-white">{t('auth.verifyEmail')}</h2>
          <p className="text-zinc-500 dark:text-zinc-300 text-sm mb-6 leading-relaxed">
            {t('auth.checkEmail')}
          </p>
          
          <div className="bg-zinc-50 dark:bg-zinc-800/50 p-4 rounded-2xl mb-8 text-left">
            <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 mb-2">{t('auth.didntReceive')}</p>
            <ul className="text-[10px] font-bold text-zinc-500 dark:text-zinc-300 space-y-1 list-disc ml-4">
              <li>{t('auth.checkSpam')}</li>
              <li>{t('auth.waitMinutes')}</li>
              <li>{t('auth.ensureEmail')}</li>
            </ul>
          </div>

          <div className="space-y-4">
            <button
              onClick={handleResendConfirmation}
              disabled={loading || resendCountdown > 0}
              className="w-full bg-zinc-900 dark:bg-amber-500 text-white dark:text-zinc-900 py-4 rounded-2xl font-black flex items-center justify-center gap-2 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all disabled:opacity-50"
            >
              {loading ? t('common.processing') : resendCountdown > 0 ? t('auth.resendIn').replace('{seconds}', resendCountdown.toString()) : t('auth.resendEmail')}
            </button>
            <button
              onClick={() => setShowConfirmationMessage(false)}
              className="w-full py-4 text-xs font-black uppercase tracking-widest text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-colors"
            >
              {t('auth.backToSignIn')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isResettingPassword) {
    return (
      <div className="w-full max-w-xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="bg-white dark:bg-zinc-900 p-6 sm:p-10 rounded-[2.5rem] sm:rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl text-center">
          <div className="inline-block p-4 bg-zinc-900 dark:bg-amber-500 text-white dark:text-zinc-900 rounded-2xl mb-6">
            <Lock size={32} aria-hidden="true" />
          </div>
          <h2 className="text-3xl font-black uppercase tracking-tighter mb-2 dark:text-white">{t('auth.resetPassword')}</h2>
          <p className="text-zinc-500 dark:text-zinc-300 text-sm mb-8">{t('auth.newPassword')}</p>

          <form onSubmit={handleSubmit(handleAuth)} className="space-y-4">
            <div className="relative text-left">
              <label htmlFor="reset-password" className={`font-black text-zinc-400 ml-4 mb-1 block ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-widest'}`}>{t('auth.password')}</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" size={18} aria-hidden="true" />
                <input
                  id="reset-password"
                  type="password"
                  aria-label={t('auth.password')}
                  className={`w-full pl-12 pr-4 py-4 bg-zinc-50 dark:bg-zinc-800 border-2 rounded-2xl focus:border-zinc-900 dark:focus:border-amber-500 outline-none transition-all font-bold dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 ${errors.password ? 'border-red-500' : 'border-transparent'}`}
                  {...register('password')}
                />
              </div>
              {errors.password && (
                <p className="text-[10px] font-black uppercase tracking-widest text-red-500 mt-1 ml-4 flex items-center gap-1">
                  <AlertCircle size={10} /> {t(errors.password.message as any)}
                </p>
              )}
              <PasswordStrengthIndicator 
                password={password} 
                strength={strength} 
                strengthLabels={strengthLabels} 
                strengthColors={strengthColors} 
              />
            </div>

            <div className="relative text-left">
              <label htmlFor="reset-confirm-password" className={`font-black text-zinc-400 ml-4 mb-1 block ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-widest'}`}>{t('auth.confirmPassword')}</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" size={18} aria-hidden="true" />
                <input
                  id="reset-confirm-password"
                  type="password"
                  aria-label={t('auth.confirmPassword')}
                  className={`w-full pl-12 pr-4 py-4 bg-zinc-50 dark:bg-zinc-800 border-2 rounded-2xl focus:border-zinc-900 dark:focus:border-amber-500 outline-none transition-all font-bold dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 ${errors.confirmPassword ? 'border-red-500' : 'border-transparent'}`}
                  {...register('confirmPassword')}
                />
              </div>
              {errors.confirmPassword && (
                <p className="text-[10px] font-black uppercase tracking-widest text-red-500 mt-1 ml-4 flex items-center gap-1">
                  <AlertCircle size={10} /> {t(errors.confirmPassword.message as any)}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-zinc-900 dark:bg-amber-500 text-white dark:text-zinc-900 py-5 rounded-2xl font-black flex items-center justify-center gap-2 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all shadow-xl shadow-zinc-200 dark:shadow-black/20 disabled:opacity-50"
            >
              {loading ? t('common.processing') : t('auth.updatePassword')}
              <Lock size={20} />
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (isForgotPassword) {
    return (
      <div className="w-full max-w-xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className={`${theme === 'dark' ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-100'} p-6 sm:p-10 rounded-[2.5rem] sm:rounded-[3rem] border shadow-2xl text-center`}>
          <div className="inline-block p-4 bg-zinc-900 dark:bg-amber-500 text-white dark:text-zinc-900 rounded-2xl mb-6">
            <Mail size={32} />
          </div>
          <h2 className="text-3xl font-black uppercase tracking-tighter mb-2 dark:text-white">{t('auth.resetPassword')}</h2>
          <p className="text-zinc-500 dark:text-zinc-300 text-sm mb-8">{t('auth.checkEmail')}</p>

          <form onSubmit={handleSubmit(handleAuth)} className="space-y-4">
            <div className="relative text-left">
              <label htmlFor="forgot-email" className={`font-black text-zinc-400 ml-4 mb-1 block ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-widest'}`}>{t('common.email')}</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                <input
                  id="forgot-email"
                  type="email"
                  className={`w-full pl-12 pr-4 py-4 bg-zinc-50 dark:bg-zinc-800 border-2 rounded-2xl focus:border-zinc-900 dark:focus:border-amber-500 outline-none transition-all font-bold dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 ${errors.email ? 'border-red-500' : 'border-transparent'}`}
                  {...register('email')}
                />
              </div>
              {errors.email && (
                <p className="text-[10px] font-black uppercase tracking-widest text-red-500 mt-1 ml-4 flex items-center gap-1">
                  <AlertCircle size={10} /> {t(errors.email.message as any)}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-zinc-900 dark:bg-amber-500 text-white dark:text-zinc-900 py-5 rounded-2xl font-black flex items-center justify-center gap-2 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all shadow-xl shadow-zinc-200 dark:shadow-black/20 disabled:opacity-50"
            >
              {loading ? t('common.processing') : t('auth.sendResetLink')}
              <Mail size={20} />
            </button>

            <button
              type="button"
              onClick={() => setIsForgotPassword(false)}
              className="w-full py-4 text-xs font-black uppercase tracking-widest text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-colors"
            >
              {t('auth.backToSignIn')}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <div className={`${theme === 'dark' ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-100'} p-6 sm:p-10 rounded-[2.5rem] sm:rounded-[3rem] border shadow-2xl text-center`}>
        <div className="flex justify-center mb-8">
          <Logo onClick={onLogoClick} />
        </div>

        <div className="flex bg-zinc-50 dark:bg-zinc-800 p-1 rounded-2xl mb-8">
          <button
            onClick={() => setCurrentRole('customer')}
            className={`flex-1 py-3 rounded-xl text-[10px] sm:text-xs font-black uppercase tracking-widest transition-all ${
              currentRole === 'customer' ? 'bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white shadow-sm' : 'text-zinc-400 hover:text-zinc-600 dark:text-zinc-300 dark:hover:text-zinc-400'
            }`}
          >
            {t('admin.customerRole')}
          </button>
          <div className="w-2" />
          <button
            onClick={() => setCurrentRole('barber_admin')}
            className={`flex-1 py-3 rounded-xl text-[10px] sm:text-xs font-black uppercase tracking-widest transition-all ${
              currentRole === 'barber_admin' ? 'bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white shadow-sm' : 'text-zinc-400 hover:text-zinc-600 dark:text-zinc-300 dark:hover:text-zinc-400'
            }`}
          >
            {t('nav.admin')}
          </button>
        </div>

        <h2 className="text-3xl font-black uppercase tracking-tighter mb-2 dark:text-white">
          {currentRole === 'barber_admin' 
            ? (isSignUp ? t('auth.joinCrew') : t('auth.adminAccess'))
            : (isSignUp ? t('auth.createAccount') : t('auth.customerLogin'))}
        </h2>
        <p className="text-zinc-500 dark:text-zinc-300 text-sm mb-8">
          {shopId && shopName && currentRole === 'customer' 
            ? t('auth.authFor').replace('{shop}', shopName) 
            : (currentRole === 'barber_admin'
              ? (isSignUp ? t('auth.createAccount') : t('auth.signInToManage'))
              : (isSignUp ? t('auth.signUpToBook') : t('auth.signInToBook')))}
        </p>

        <form onSubmit={handleSubmit(handleAuth)} className="space-y-4">
          {isSignUp && currentRole === 'barber_admin' && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="relative text-left">
                  <label className={`font-black text-zinc-400 ml-4 mb-1 block ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-widest'}`}>{t('booking.firstName')}</label>
                  <input
                    type="text"
                    className={`w-full px-4 py-4 bg-zinc-50 dark:bg-zinc-800 border-2 rounded-2xl focus:border-zinc-900 dark:focus:border-amber-500 outline-none transition-all font-bold dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 ${errors.firstName ? 'border-red-500' : 'border-transparent'}`}
                    {...register('firstName')}
                  />
                  {errors.firstName && (
                    <p className="text-[10px] font-black uppercase tracking-widest text-red-500 mt-1 ml-4 flex items-center gap-1">
                      <AlertCircle size={10} /> {t(errors.firstName.message as any)}
                    </p>
                  )}
                </div>
                <div className="relative text-left">
                  <label className={`font-black text-zinc-400 ml-4 mb-1 block ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-widest'}`}>{t('booking.lastName')}</label>
                  <input
                    type="text"
                    className={`w-full px-4 py-4 bg-zinc-50 dark:bg-zinc-800 border-2 rounded-2xl focus:border-zinc-900 dark:focus:border-amber-500 outline-none transition-all font-bold dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 ${errors.lastName ? 'border-red-500' : 'border-transparent'}`}
                    {...register('lastName')}
                  />
                  {errors.lastName && (
                    <p className="text-[10px] font-black uppercase tracking-widest text-red-500 mt-1 ml-4 flex items-center gap-1">
                      <AlertCircle size={10} /> {t(errors.lastName.message as any)}
                    </p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="relative text-left">
                  <label className={`font-black text-zinc-400 ml-4 mb-1 block ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-widest'}`}>{t('booking.companyName')}</label>
                  <input
                    type="text"
                    className={`w-full px-4 py-4 bg-zinc-50 dark:bg-zinc-800 border-2 rounded-2xl focus:border-zinc-900 dark:focus:border-amber-500 outline-none transition-all font-bold dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 ${errors.companyName ? 'border-red-500' : 'border-transparent'}`}
                    {...register('companyName')}
                  />
                  {errors.companyName && (
                    <p className="text-[10px] font-black uppercase tracking-widest text-red-500 mt-1 ml-4 flex items-center gap-1">
                      <AlertCircle size={10} /> {t(errors.companyName.message as any)}
                    </p>
                  )}
                </div>
                <div className="relative text-left">
                  <label className={`font-black text-zinc-400 ml-4 mb-1 block ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-widest'}`}>{t('booking.address')}</label>
                  <input
                    type="text"
                    className={`w-full px-4 py-4 bg-zinc-50 dark:bg-zinc-800 border-2 rounded-2xl focus:border-zinc-900 dark:focus:border-amber-500 outline-none transition-all font-bold dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 ${errors.address ? 'border-red-500' : 'border-transparent'}`}
                    {...register('address')}
                  />
                  {errors.address && (
                    <p className="text-[10px] font-black uppercase tracking-widest text-red-500 mt-1 ml-4 flex items-center gap-1">
                      <AlertCircle size={10} /> {t(errors.address.message as any)}
                    </p>
                  )}
                </div>
              </div>

              <div className="p-6 bg-amber-50 dark:bg-amber-900/20 rounded-[2rem] border border-amber-100 dark:border-amber-900/30 mt-6">
                <p className="text-[10px] font-black uppercase tracking-widest text-amber-600 dark:text-amber-500 mb-3 flex items-center gap-2">
                  <CreditCard size={14} /> {t('pricing.fourteenDayTrial')}
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400/80 font-bold leading-relaxed">
                  {language === 'de' 
                    ? 'Starten Sie heute Ihre 14-tägige kostenlose Testphase. Sie werden zur sicheren Zahlung über Stripe weitergeleitet, aber erst nach Ablauf der Testphase belastet.'
                    : language === 'fr'
                    ? 'Commencez votre essai gratuit de 14 jours dès aujourd\'hui. Vous serez redirigé vers le paiement sécurisé via Stripe, mais vous ne serez facturé qu\'après la fin de l\'essai.'
                    : language === 'it'
                    ? 'Inizia oggi la tua prova gratuita di 14 giorni. Verrai reindirizzato al pagamento sicuro tramite Stripe, ma ti verrà addebitato solo al termine della prova.'
                    : 'Start your 14-day free trial today. You will be redirected to secure payment via Stripe, but you will not be charged until the trial ends.'}
                </p>
                <div className="flex items-center gap-3 mt-4 opacity-80">
                  <div className="h-8 px-3 bg-white dark:bg-zinc-900 rounded-lg flex items-center justify-center shadow-sm border border-zinc-100 dark:border-zinc-800">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg" alt="Stripe" className="h-4" referrerPolicy="no-referrer" />
                  </div>
                </div>
              </div>
            </>
          )}

          {isSignUp && currentRole === 'customer' && (
            <div className="relative text-left">
              <label className={`font-black text-zinc-400 ml-4 mb-1 block ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-widest'}`}>{t('booking.fullName')}</label>
              <input
                type="text"
                className={`w-full px-4 py-4 bg-zinc-50 dark:bg-zinc-800 border-2 rounded-2xl focus:border-zinc-900 dark:focus:border-amber-500 outline-none transition-all font-bold dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 ${errors.fullName ? 'border-red-500' : 'border-transparent'}`}
                {...register('fullName')}
              />
              {errors.fullName && (
                <p className="text-[10px] font-black uppercase tracking-widest text-red-500 mt-1 ml-4 flex items-center gap-1">
                  <AlertCircle size={10} /> {t(errors.fullName.message as any)}
                </p>
              )}
            </div>
          )}

          {isSignUp && (
            <div className="relative text-left">
              <label className={`font-black text-zinc-400 ml-4 mb-1 block ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-widest'}`}>{t('booking.phoneNumber')}</label>
              <input
                type="tel"
                className={`w-full px-4 py-4 bg-zinc-50 dark:bg-zinc-800 border-2 rounded-2xl focus:border-zinc-900 dark:focus:border-amber-500 outline-none transition-all font-bold dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 ${errors.phone ? 'border-red-500' : 'border-transparent'}`}
                {...register('phone')}
              />
              {errors.phone && (
                <p className="text-[10px] font-black uppercase tracking-widest text-red-500 mt-1 ml-4 flex items-center gap-1">
                  <AlertCircle size={10} /> {t(errors.phone.message as any)}
                </p>
              )}
            </div>
          )}

          <div className="relative text-left">
            <label htmlFor="auth-email" className={`font-black text-zinc-400 ml-4 mb-1 block ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-widest'}`}>{t('common.email')}</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" size={18} aria-hidden="true" />
              <input
                id="auth-email"
                type="email"
                aria-label={t('common.email')}
                className={`w-full pl-12 pr-4 py-4 bg-zinc-50 dark:bg-zinc-800 border-2 rounded-2xl focus:border-zinc-900 dark:focus:border-amber-500 outline-none transition-all font-bold dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 ${errors.email ? 'border-red-500' : 'border-transparent'}`}
                {...register('email')}
              />
            </div>
            {errors.email && (
              <p className="text-[10px] font-black uppercase tracking-widest text-red-500 mt-1 ml-4 flex items-center gap-1">
                <AlertCircle size={10} /> {t(errors.email.message as any)}
              </p>
            )}
          </div>

          <div className="relative text-left">
            <label htmlFor="auth-password" className={`font-black text-zinc-400 ml-4 mb-1 block ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-widest'}`}>{t('auth.password')}</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" size={18} aria-hidden="true" />
              <input
                id="auth-password"
                type="password"
                aria-label={t('auth.password')}
                className={`w-full pl-12 pr-4 py-4 bg-zinc-50 dark:bg-zinc-800 border-2 rounded-2xl focus:border-zinc-900 dark:focus:border-amber-500 outline-none transition-all font-bold dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 ${errors.password ? 'border-red-500' : 'border-transparent'}`}
                {...register('password')}
              />
            </div>
            {errors.password && (
              <p className="text-[10px] font-black uppercase tracking-widest text-red-500 mt-1 ml-4 flex items-center gap-1">
                <AlertCircle size={10} /> {t(errors.password.message as any)}
              </p>
            )}
            <PasswordStrengthIndicator 
              password={password} 
              strength={strength} 
              strengthLabels={strengthLabels} 
              strengthColors={strengthColors} 
            />
            {!isSignUp && (
              <button
                type="button"
                onClick={() => setIsForgotPassword(true)}
                className="mt-2 ml-4 text-[10px] font-black uppercase tracking-widest text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-colors"
              >
                {t('auth.forgotPassword')}
              </button>
            )}
          </div>

          {isSignUp && (
            <div className="relative text-left">
              <label htmlFor="auth-confirm-password" className={`font-black text-zinc-400 ml-4 mb-1 block ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-widest'}`}>{t('auth.confirmPassword')}</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                <input
                  id="auth-confirm-password"
                  type="password"
                  className={`w-full pl-12 pr-4 py-4 bg-zinc-50 dark:bg-zinc-800 border-2 rounded-2xl focus:border-zinc-900 dark:focus:border-amber-500 outline-none transition-all font-bold dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 ${errors.confirmPassword ? 'border-red-500' : 'border-transparent'}`}
                  {...register('confirmPassword')}
                />
              </div>
              {errors.confirmPassword && (
                <p className="text-[10px] font-black uppercase tracking-widest text-red-500 mt-1 ml-4 flex items-center gap-1">
                  <AlertCircle size={10} /> {t(errors.confirmPassword.message as any)}
                </p>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-zinc-900 dark:bg-amber-500 text-white dark:text-zinc-900 py-5 rounded-2xl font-black flex items-center justify-center gap-2 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all shadow-xl shadow-zinc-200 dark:shadow-black/20 disabled:opacity-50"
          >
            {loading ? t('common.processing') : isSignUp ? t('auth.signUp') : t('auth.signIn')}
            {isSignUp ? <UserPlus size={20} /> : <LogIn size={20} />}
          </button>
        </form>

        <button
          onClick={() => setIsSignUp(!isSignUp)}
          className="mt-6 text-xs font-black uppercase tracking-widest text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-colors"
        >
          {isSignUp ? t('auth.alreadyHaveAccount') : t('auth.dontHaveAccount')}
        </button>
      </div>
    </div>
  );
}
